// The unwrap moment (A3): the first time a child opens the app after a stage that was wrapped for
// them has opened, it unwraps once (paper tears, the island pops up). Surprise plans get a small
// version on Trip · Today. Pure: what to show and what to mark as seen; the phone keeps the seen
// lists per child (kv).
import type { Visibility } from '../../../shared/controls'
import type { DayItem, Stage } from '../../../shared/pack/schema'
import { addDays } from '../time'
import { revealed, stageReveals, type TripClock } from './visible'

/** Older reveals than this are marked seen quietly (a phone set up mid-trip doesn't replay them). */
export const UNWRAP_WITHIN_DAYS = 3

export interface UnwrapDue {
  /** The stage to unwrap now (the latest that opened), or null. */
  show: Stage | null
  /** Every stage id to mark as seen (shown or quietly skipped). */
  seen: string[]
}

/**
 * Stages that were wrapped for this child (their visibility gives them a reveal date) and have
 * opened since they last looked. Only the latest plays; any others are marked seen with it.
 */
export function unwrapDue(stages: readonly Stage[], v: Visibility, now: TripClock, seen: ReadonlySet<string>): UnwrapDue {
  const opens = stageReveals(stages, v)
  const opened = stages.flatMap((s) => {
    const r = opens.get(s.id)
    return r && revealed(r, now) && !seen.has(s.id) ? [{ s, r }] : []
  })
  if (!opened.length) return { show: null, seen: [] }
  const latest = opened.reduce((a, b) => (b.r >= a.r ? b : a))
  const recent = latest.r.slice(0, 10) >= addDays(now.date, -UNWRAP_WITHIN_DAYS)
  return { show: recent ? latest.s : null, seen: opened.map((o) => o.s.id) }
}

/** Surprise plans that opened today and haven't burst open on this phone yet (Trip · Today). */
export function surprisesJustOpened(items: readonly DayItem[], now: TripClock, seen: ReadonlySet<string>): string[] {
  return items.filter((i) => i.surprise && i.surprise.until.slice(0, 10) === now.date && revealed(i.surprise.until, now) && !seen.has(i.id)).map((i) => i.id)
}
